<template>
  <v-app>
    <v-container fluid class="fhc-enrollment">
      <!-- Public Header with Logo and Info -->
      <v-row>
        <v-col cols="12">
          <v-card class="header-card" elevation="4" color="primary" dark>
            <v-row align="center" class="pa-4">
              <v-col cols="12" md="8">
                <div class="d-flex align-center">
                  <v-icon x-large class="mr-4">mdi-hospital-building</v-icon>
                  <div>
                    <div class="text-h3 font-weight-bold">Family Health Choice</div>
                    <div class="text-h6 mt-2">Online Enrollment Application</div>
                    <div class="text-subtitle-2 mt-1 grey--text text--lighten-2">
                      Complete your enrollment in just a few minutes
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-right">
                <v-btn text color="white" small @click="goToLogin" class="mb-2">
                  <v-icon left small>mdi-login</v-icon>
                  Staff Login
                </v-btn>
                <div class="text-caption">Need help?</div>
                <div class="text-h6">1-800-FHC-HELP</div>
                <div class="text-caption">Mon-Fri 9AM-6PM EST</div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

    <!-- Stepper -->
    <v-row>
      <v-col cols="12">
        <v-card elevation="2">
          <v-stepper v-model="step" alt-labels>
            <v-stepper-header>
              <v-stepper-step :complete="step > 1" step="1" color="primary">
                Select Plan
              </v-stepper-step>
              <v-divider></v-divider>
              
              <v-stepper-step :complete="step > 2" step="2" color="primary">
                Personal Info
              </v-stepper-step>
              <v-divider></v-divider>
              
              <v-stepper-step :complete="step > 3" step="3" color="primary">
                Contact Info
              </v-stepper-step>
              <v-divider></v-divider>
              
              <v-stepper-step :complete="step > 4" step="4" color="primary">
                Dependents
              </v-stepper-step>
              <v-divider></v-divider>
              
              <v-stepper-step :complete="step > 5" step="5" color="primary">
                Payment
              </v-stepper-step>
              <v-divider></v-divider>
              
              <v-stepper-step step="6" color="primary">
                Review & Sign
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <!-- Step 1: Plan Selection -->
              <v-stepper-content step="1">
                <v-card elevation="0" class="pa-4">
                  <v-card-title class="text-h5 primary--text mb-4">
                    Choose Your Health Plan
                  </v-card-title>
                  
                  <v-row>
                    <v-col cols="12" md="6" v-for="plan in availablePlans" :key="plan.type">
                      <v-card 
                        class="plan-card" 
                        :class="{ 'selected-plan': enrollment.planType === plan.type }"
                        @click="selectPlan(plan)"
                        hover
                        elevation="2"
                      >
                        <v-card-title class="d-flex align-center">
                          <v-icon large :color="enrollment.planType === plan.type ? 'primary' : 'grey'" left>
                            {{ plan.icon }}
                          </v-icon>
                          <span>{{ plan.name }}</span>
                          <v-spacer></v-spacer>
                          <v-icon v-if="enrollment.planType === plan.type" color="success">
                            mdi-check-circle
                          </v-icon>
                        </v-card-title>
                        
                        <v-card-subtitle>
                          {{ plan.description }}
                        </v-card-subtitle>
                        
                        <v-card-text>
                          <v-chip small color="primary" outlined class="mr-2">
                            Starting at ${{ plan.startingPrice }}/month
                          </v-chip>
                          <v-chip small color="secondary" outlined>
                            ${{ plan.applicationFee }} application fee
                          </v-chip>
                          
                          <v-divider class="my-3"></v-divider>
                          
                          <div class="benefits-list">
                            <div v-for="benefit in plan.benefits" :key="benefit" class="d-flex align-center mb-1">
                              <v-icon small color="success" class="mr-2">mdi-check</v-icon>
                              <span class="text-caption">{{ benefit }}</span>
                            </div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card>
                
                <v-card-actions class="mt-4">
                  <v-spacer></v-spacer>
                  <v-btn color="primary" large @click="nextStep" :disabled="!enrollment.planType">
                    Continue
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-stepper-content>

              <!-- Step 2: Personal Information -->
              <v-stepper-content step="2">
                <v-card elevation="0" class="pa-4">
                  <v-card-title class="text-h5 primary--text mb-4">
                    Personal Information
                  </v-card-title>
                  
                  <v-form ref="personalForm" v-model="personalFormValid">
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="enrollment.firstName"
                          label="First Name"
                          prepend-icon="mdi-account"
                          outlined
                          dense
                          :rules="[rules.required]"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="enrollment.lastName"
                          label="Last Name"
                          outlined
                          dense
                          :rules="[rules.required]"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="4">
                        <v-menu
                          v-model="dobMenu"
                          :close-on-content-click="false"
                          max-width="290px"
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              :value="formattedDOB"
                              label="Date of Birth"
                              prepend-icon="mdi-calendar"
                              readonly
                              outlined
                              dense
                              v-bind="attrs"
                              v-on="on"
                              :rules="[rules.required]"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="enrollment.dateOfBirth"
                            @input="dobMenu = false"
                            :max="new Date().toISOString().substr(0, 10)"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                      
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="enrollment.ssn"
                          label="SSN (Optional)"
                          prepend-icon="mdi-card-account-details"
                          outlined
                          dense
                          placeholder="XXX-XX-XXXX"
                          v-mask="'###-##-####'"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="enrollment.driverLicense"
                          label="Driver's License #"
                          prepend-icon="mdi-card-account-details-outline"
                          outlined
                          dense
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="enrollment.maritalStatus"
                          label="Marital Status"
                          :items="['Single', 'Married', 'Divorced', 'Widowed']"
                          prepend-icon="mdi-heart"
                          outlined
                          dense
                          :rules="[rules.required]"
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-card>
                
                <v-card-actions class="mt-4">
                  <v-btn text @click="prevStep">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" large @click="nextStep" :disabled="!personalFormValid">
                    Continue
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-stepper-content>

              <!-- Step 3: Contact Information -->
              <v-stepper-content step="3">
                <v-card elevation="0" class="pa-4">
                  <v-card-title class="text-h5 primary--text mb-4">
                    Contact Information
                  </v-card-title>
                  
                  <v-form ref="contactForm" v-model="contactFormValid">
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="enrollment.streetAddress"
                          label="Street Address"
                          prepend-icon="mdi-home"
                          outlined
                          dense
                          :rules="[rules.required]"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="5">
                        <v-text-field
                          v-model="enrollment.city"
                          label="City"
                          prepend-icon="mdi-city"
                          outlined
                          dense
                          :rules="[rules.required]"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="enrollment.state"
                          label="State"
                          outlined
                          dense
                          :rules="[rules.required]"
                          maxlength="2"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="enrollment.zipCode"
                          label="Zip Code"
                          outlined
                          dense
                          :rules="[rules.required, rules.zipCode]"
                          v-mask="'#####'"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="enrollment.homePhone"
                          label="Home Phone"
                          prepend-icon="mdi-phone"
                          outlined
                          dense
                          v-mask="'(###) ###-####'"
                          placeholder="(XXX) XXX-XXXX"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="enrollment.cellPhone"
                          label="Cell Phone"
                          prepend-icon="mdi-cellphone"
                          outlined
                          dense
                          :rules="[rules.required, rules.phone]"
                          v-mask="'(###) ###-####'"
                          placeholder="(XXX) XXX-XXXX"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="enrollment.email"
                          label="Email"
                          prepend-icon="mdi-email"
                          outlined
                          dense
                          :rules="[rules.required, rules.email]"
                          type="email"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="enrollment.employerName"
                          label="Employer Name (Optional)"
                          prepend-icon="mdi-briefcase"
                          outlined
                          dense
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="enrollment.workPhone"
                          label="Work Phone (Optional)"
                          prepend-icon="mdi-phone-classic"
                          outlined
                          dense
                          v-mask="'(###) ###-####'"
                          placeholder="(XXX) XXX-XXXX"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-card>
                
                <v-card-actions class="mt-4">
                  <v-btn text @click="prevStep">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" large @click="nextStep" :disabled="!contactFormValid">
                    Continue
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-stepper-content>

              <!-- Step 4: Dependents -->
              <v-stepper-content step="4">
                <v-card elevation="0" class="pa-4">
                  <v-card-title class="text-h5 primary--text mb-4">
                    Legal Dependents
                    <v-spacer></v-spacer>
                    <v-btn 
                      color="primary" 
                      @click="addDependent"
                      :disabled="enrollment.dependents.length >= 4"
                      small
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Add Dependent
                    </v-btn>
                  </v-card-title>
                  
                  <v-alert v-if="enrollment.dependents.length === 0" type="info" text>
                    <strong>Optional:</strong> Add up to 4 legal dependents (spouse and children)
                  </v-alert>
                  
                  <v-expansion-panels v-model="dependentPanels" multiple class="mb-4">
                    <v-expansion-panel
                      v-for="(dependent, index) in enrollment.dependents"
                      :key="index"
                    >
                      <v-expansion-panel-header>
                        <div class="d-flex align-center">
                          <v-icon left :color="dependent.relationship === 'Spouse' ? 'pink' : 'blue'">
                            {{ dependent.relationship === 'Spouse' ? 'mdi-account-heart' : 'mdi-account-child' }}
                          </v-icon>
                          <span class="font-weight-medium">
                            {{ dependent.firstName }} {{ dependent.lastName }}
                            <v-chip x-small class="ml-2">{{ dependent.relationship }}</v-chip>
                          </span>
                        </div>
                      </v-expansion-panel-header>
                      
                      <v-expansion-panel-content>
                        <v-row>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="dependent.firstName"
                              label="First Name"
                              outlined
                              dense
                              :rules="[rules.required]"
                            ></v-text-field>
                          </v-col>
                          
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="dependent.lastName"
                              label="Last Name"
                              outlined
                              dense
                              :rules="[rules.required]"
                            ></v-text-field>
                          </v-col>
                          
                          <v-col cols="12" md="4">
                            <v-menu
                              :close-on-content-click="false"
                              max-width="290px"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  :value="formatDate(dependent.dateOfBirth)"
                                  label="Date of Birth"
                                  prepend-icon="mdi-calendar"
                                  readonly
                                  outlined
                                  dense
                                  v-bind="attrs"
                                  v-on="on"
                                  :rules="[rules.required]"
                                ></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="dependent.dateOfBirth"
                                :max="new Date().toISOString().substr(0, 10)"
                              ></v-date-picker>
                            </v-menu>
                          </v-col>
                          
                          <v-col cols="12" md="6">
                            <v-select
                              v-model="dependent.relationship"
                              label="Relationship"
                              :items="['Spouse', 'Child']"
                              outlined
                              dense
                              :rules="[rules.required]"
                            ></v-select>
                          </v-col>
                          
                          <v-col cols="12" md="6" v-if="dependent.relationship === 'Child'">
                            <v-select
                              v-model="dependent.gender"
                              label="Gender"
                              :items="[{text: 'Male', value: 'M'}, {text: 'Female', value: 'F'}]"
                              outlined
                              dense
                              :rules="[rules.required]"
                            ></v-select>
                          </v-col>
                          
                          <v-col cols="12">
                            <v-btn 
                              color="error" 
                              text 
                              small 
                              @click="removeDependent(index)"
                            >
                              <v-icon left small>mdi-delete</v-icon>
                              Remove Dependent
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-card>
                
                <v-card-actions class="mt-4">
                  <v-btn text @click="prevStep">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" large @click="nextStep">
                    Continue
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-stepper-content>

              <!-- Step 5: Payment -->
              <v-stepper-content step="5">
                <v-card elevation="0" class="pa-4">
                  <v-card-title class="text-h5 primary--text mb-4">
                    Payment Information
                  </v-card-title>
                  
                  <v-form ref="paymentForm" v-model="paymentFormValid">
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="enrollment.familySize"
                          label="Coverage Type"
                          :items="['Single', 'Couple', 'Family']"
                          prepend-icon="mdi-account-group"
                          outlined
                          dense
                          :rules="[rules.required]"
                          @change="calculatePayment"
                        ></v-select>
                      </v-col>
                      
                      <v-col cols="12" md="4" v-if="needsAgeGroup">
                        <v-select
                          v-model="enrollment.ageGroup"
                          label="Age Group"
                          :items="['Under 18', '19-34', '35-45', '46-55', '56+']"
                          prepend-icon="mdi-account-clock"
                          outlined
                          dense
                          :rules="[rules.required]"
                          @change="calculatePayment"
                        ></v-select>
                      </v-col>
                      
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model.number="enrollment.payment.monthsPaid"
                          label="Months to Pay"
                          prepend-icon="mdi-calendar-month"
                          outlined
                          dense
                          type="number"
                          :rules="[rules.required, rules.minValue(1)]"
                          @input="calculatePayment"
                          min="1"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12">
                        <v-divider class="mb-4"></v-divider>
                        <v-card color="grey lighten-4" class="pa-4">
                          <v-row>
                            <v-col cols="12" md="4">
                              <div class="text-subtitle-2 grey--text text--darken-2">Monthly Fee</div>
                              <div class="text-h6 primary--text font-weight-bold">
                                ${{ enrollment.payment.monthlyFee.toFixed(2) }}
                              </div>
                            </v-col>
                            
                            <v-col cols="12" md="4">
                              <div class="text-subtitle-2 grey--text text--darken-2">Application Fee</div>
                              <div class="text-h6 primary--text font-weight-bold">
                                ${{ enrollment.payment.applicationFee.toFixed(2) }}
                              </div>
                            </v-col>
                            
                            <v-col cols="12" md="4">
                              <div class="text-subtitle-2 grey--text text--darken-2">Total Payment</div>
                              <div class="text-h5 success--text font-weight-bold">
                                ${{ calculatedTotal.toFixed(2) }}
                              </div>
                            </v-col>
                          </v-row>
                        </v-card>
                      </v-col>
                      
                      <v-col cols="12">
                        <v-radio-group
                          v-model="enrollment.payment.method"
                          label="Payment Method"
                          row
                          :rules="[rules.required]"
                        >
                          <v-radio label="Credit Card" value="Credit Card" color="primary">
                            <template v-slot:label>
                              <div class="d-flex align-center">
                                <v-icon left>mdi-credit-card</v-icon>
                                Credit Card
                              </div>
                            </template>
                          </v-radio>
                          <v-radio label="Debit Card" value="Debit Card" color="primary">
                            <template v-slot:label>
                              <div class="d-flex align-center">
                                <v-icon left>mdi-credit-card-outline</v-icon>
                                Debit Card
                              </div>
                            </template>
                          </v-radio>
                          <v-radio label="Check" value="Check" color="primary">
                            <template v-slot:label>
                              <div class="d-flex align-center">
                                <v-icon left>mdi-checkbook</v-icon>
                                Check
                              </div>
                            </template>
                          </v-radio>
                          <v-radio label="Cash" value="Cash" color="primary">
                            <template v-slot:label>
                              <div class="d-flex align-center">
                                <v-icon left>mdi-cash</v-icon>
                                Cash
                              </div>
                            </template>
                          </v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-card>
                
                <v-card-actions class="mt-4">
                  <v-btn text @click="prevStep">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" large @click="nextStep" :disabled="!paymentFormValid">
                    Continue
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-stepper-content>

              <!-- Step 6: Review & Sign -->
              <v-stepper-content step="6">
                <v-card elevation="0" class="pa-4">
                  <v-card-title class="text-h5 primary--text mb-4">
                    Review & Sign
                  </v-card-title>
                  
                  <!-- Summary Review -->
                  <v-card outlined class="mb-4">
                    <v-card-title class="subtitle-1 primary white--text">
                      <v-icon left color="white">mdi-clipboard-check</v-icon>
                      Enrollment Summary
                    </v-card-title>
                    
                    <v-card-text>
                      <v-row dense>
                        <v-col cols="12" md="6">
                          <div class="summary-section">
                            <div class="summary-label">Plan Type</div>
                            <div class="summary-value">{{ enrollment.planType }}</div>
                          </div>
                        </v-col>
                        
                        <v-col cols="12" md="6">
                          <div class="summary-section">
                            <div class="summary-label">Applicant Name</div>
                            <div class="summary-value">{{ enrollment.firstName }} {{ enrollment.lastName }}</div>
                          </div>
                        </v-col>
                        
                        <v-col cols="12" md="6">
                          <div class="summary-section">
                            <div class="summary-label">Date of Birth</div>
                            <div class="summary-value">{{ formattedDOB }}</div>
                          </div>
                        </v-col>
                        
                        <v-col cols="12" md="6">
                          <div class="summary-section">
                            <div class="summary-label">Email</div>
                            <div class="summary-value">{{ enrollment.email }}</div>
                          </div>
                        </v-col>
                        
                        <v-col cols="12">
                          <div class="summary-section">
                            <div class="summary-label">Address</div>
                            <div class="summary-value">
                              {{ enrollment.streetAddress }}, {{ enrollment.city }}, {{ enrollment.state }} {{ enrollment.zipCode }}
                            </div>
                          </div>
                        </v-col>
                        
                        <v-col cols="12" v-if="enrollment.dependents.length > 0">
                          <div class="summary-section">
                            <div class="summary-label">Dependents ({{ enrollment.dependents.length }})</div>
                            <div class="summary-value">
                              <v-chip
                                v-for="(dep, idx) in enrollment.dependents"
                                :key="idx"
                                small
                                class="mr-2 mb-2"
                              >
                                {{ dep.firstName }} {{ dep.lastName }} ({{ dep.relationship }})
                              </v-chip>
                            </div>
                          </div>
                        </v-col>
                        
                        <v-col cols="12" md="6">
                          <div class="summary-section">
                            <div class="summary-label">Coverage Type</div>
                            <div class="summary-value">{{ enrollment.familySize }}</div>
                          </div>
                        </v-col>
                        
                        <v-col cols="12" md="6">
                          <div class="summary-section">
                            <div class="summary-label">Payment Method</div>
                            <div class="summary-value">{{ enrollment.payment.method }}</div>
                          </div>
                        </v-col>
                        
                        <v-col cols="12">
                          <v-divider class="my-2"></v-divider>
                          <div class="text-h6 success--text text-center">
                            Total Payment: ${{ calculatedTotal.toFixed(2) }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                  
                  <!-- Signature -->
                  <v-card outlined>
                    <v-card-title class="subtitle-1">
                      <v-icon left>mdi-draw</v-icon>
                      Applicant Signature
                    </v-card-title>
                    
                    <v-card-text>
                      <v-row>
                        <v-col cols="12">
                          <div class="signature-pad-container">
                            <canvas
                              ref="signatureCanvas"
                              class="signature-canvas"
                              @mousedown="startDrawing"
                              @mousemove="draw"
                              @mouseup="stopDrawing"
                              @mouseleave="stopDrawing"
                              @touchstart="startDrawing"
                              @touchmove="draw"
                              @touchend="stopDrawing"
                            ></canvas>
                          </div>
                          <div class="d-flex justify-space-between mt-2">
                            <v-btn text small @click="clearSignature" color="error">
                              <v-icon left small>mdi-eraser</v-icon>
                              Clear
                            </v-btn>
                            <span class="text-caption grey--text">
                              Sign above using your mouse or touchscreen
                            </span>
                          </div>
                        </v-col>
                        
                        <v-col cols="12" md="6">
                          <v-menu
                            :close-on-content-click="false"
                            max-width="290px"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                :value="formatDate(enrollment.signatureDate)"
                                label="Signature Date"
                                prepend-icon="mdi-calendar"
                                readonly
                                outlined
                                dense
                                v-bind="attrs"
                                v-on="on"
                                :rules="[rules.required]"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="enrollment.signatureDate"
                              :max="new Date().toISOString().substr(0, 10)"
                            ></v-date-picker>
                          </v-menu>
                        </v-col>
                      </v-row>
                      
                      <v-alert type="info" text dense class="mt-4">
                        <strong>Disclosure:</strong> This is NOT INSURANCE. This plan provides discounts at certain health care providers.
                        By signing, you acknowledge you have read and agree to the terms and conditions.
                      </v-alert>
                    </v-card-text>
                  </v-card>
                </v-card>
                
                <v-card-actions class="mt-4">
                  <v-btn text @click="prevStep">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn 
                    color="grey" 
                    large 
                    @click="saveDraft"
                    :loading="saving"
                    outlined
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    Save Draft
                  </v-btn>
                  <v-btn 
                    color="success" 
                    large 
                    @click="submitEnrollment"
                    :loading="submitting"
                    :disabled="!canSubmit"
                  >
                    <v-icon left>mdi-send</v-icon>
                    Submit Enrollment
                  </v-btn>
                </v-card-actions>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h5 success white--text">
          <v-icon large left color="white">mdi-check-circle</v-icon>
          Enrollment Submitted!
        </v-card-title>
        <v-card-text class="pa-6">
          <div class="text-center">
            <v-icon size="80" color="success" class="mb-4">mdi-check-circle-outline</v-icon>
            <p class="text-h6">Your enrollment application has been submitted successfully!</p>
            <p class="text-body-2 grey--text">
              Enrollment ID: <strong>{{ submittedEnrollmentId }}</strong>
            </p>
            <p class="text-body-2 mt-4">
              You will receive a confirmation email shortly. Our team will review your application and contact you soon.
            </p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="resetForm">
            <v-icon left>mdi-plus</v-icon>
            New Enrollment
          </v-btn>
          <v-btn color="grey" text @click="goToDashboard">
            Go to Dashboard
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FHCEnrollment',
  
  data() {
    return {
      step: 1,
      dobMenu: false,
      dependentPanels: [],
      personalFormValid: false,
      contactFormValid: false,
      paymentFormValid: false,
      saving: false,
      submitting: false,
      successDialog: false,
      submittedEnrollmentId: null,
      
      // Drawing signature
      isDrawing: false,
      signatureContext: null,
      
      enrollment: {
        // Personal Info
        firstName: '',
        lastName: '',
        dateOfBirth: null,
        ssn: '',
        driverLicense: '',
        maritalStatus: '',
        
        // Contact Info
        streetAddress: '',
        city: '',
        state: 'FL',
        zipCode: '',
        homePhone: '',
        cellPhone: '',
        email: '',
        employerName: '',
        workPhone: '',
        
        // Dependents
        dependents: [],
        
        // Plan Info
        planType: '',
        ageGroup: '',
        familySize: 'Single',
        
        // Payment
        payment: {
          method: '',
          monthsPaid: 1,
          monthlyFee: 0,
          applicationFee: 0,
          totalPayment: 0
        },
        
        // Signature
        signature: '',
        signatureDate: new Date().toISOString().substr(0, 10),
        
        // Status
        status: 'Draft'
      },
      
      // Validation rules
      rules: {
        required: value => !!value || 'Required field',
        email: value => {
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return pattern.test(value) || 'Invalid email';
        },
        phone: value => {
          const pattern = /^\(\d{3}\) \d{3}-\d{4}$/;
          return !value || pattern.test(value) || 'Invalid phone format';
        },
        zipCode: value => {
          const pattern = /^\d{5}$/;
          return pattern.test(value) || 'Invalid zip code';
        },
        minValue: min => value => value >= min || `Must be at least ${min}`
      },
      
      // Available plans
      availablePlans: [
        {
          type: 'DPO - Discount Plan',
          name: 'Discount Plan Organization',
          description: 'Medical, Dental, Vision, Pharmacy & Laboratory Savings',
          startingPrice: 35.99,
          applicationFee: 30,
          icon: 'mdi-medical-bag',
          benefits: [
            'Medical Savings',
            'Dental Savings',
            'Vision Savings',
            'Pharmacy Savings',
            'Laboratory Savings'
          ]
        },
        {
          type: 'Hospital Medical Advocacy',
          name: 'Hospital Medical Advocacy',
          description: 'Outstanding Medical Bill Negotiation Services',
          startingPrice: 9.99,
          applicationFee: 30,
          icon: 'mdi-hospital-building',
          benefits: [
            'Medical Bill Negotiation',
            'Hospital Advocacy',
            'Insurance Assistance'
          ]
        },
        {
          type: 'Pharmacy Services',
          name: 'Pharmacy Cost CareRX',
          description: 'Prescription Drug Discount Services',
          startingPrice: 10.00,
          applicationFee: 25,
          icon: 'mdi-pill',
          benefits: [
            'Pharmacy Discounts',
            'Prescription Savings',
            'Nationwide Coverage'
          ]
        },
        {
          type: 'TeleMed Urgent Care',
          name: 'TeleMed Urgent Care',
          description: '24/7 Virtual Doctor Consultations',
          startingPrice: 6.00,
          applicationFee: 30,
          icon: 'mdi-video',
          benefits: [
            '24/7 TeleMed Access',
            'Urgent Care Consultations',
            'No Wait Times'
          ]
        },
        {
          type: 'Dental Services - Core Care',
          name: 'Core Care Dental Plan',
          description: 'Basic Dental Coverage',
          startingPrice: 6.99,
          applicationFee: 30,
          icon: 'mdi-tooth',
          benefits: [
            'Preventive Care',
            'Basic Procedures',
            'Network Dentists'
          ]
        },
        {
          type: 'PHC Basic Plan',
          name: 'Prepaid Health Clinic - Basic',
          description: 'Comprehensive Primary Care Services',
          startingPrice: 45.00,
          applicationFee: 25,
          icon: 'mdi-hospital',
          benefits: [
            'Physician Care',
            'Specialty Care',
            'Pharmacy & Vision',
            'Dental & Laboratory',
            'Hospital Advocacy'
          ],
          requiresAge: true
        },
        {
          type: 'PHC Plus Plan',
          name: 'Prepaid Health Clinic - Plus',
          description: 'Enhanced Primary Care with Additional Benefits',
          startingPrice: 55.00,
          applicationFee: 25,
          icon: 'mdi-hospital-marker',
          benefits: [
            'All Basic Plan Benefits',
            'Enhanced Coverage',
            'Priority Scheduling',
            'Extended Services'
          ],
          requiresAge: true
        },
        {
          type: 'PHC Premium Plan',
          name: 'Prepaid Health Clinic - Premium',
          description: 'Premium Care with 3 Specialty Visits',
          startingPrice: 70.00,
          applicationFee: 25,
          icon: 'mdi-hospital-box',
          benefits: [
            'All Plus Plan Benefits',
            '3 Specialty Visits',
            'Premium Services',
            'Priority Access'
          ],
          requiresAge: true
        }
      ]
    };
  },
  
  computed: {
    formattedDOB() {
      if (!this.enrollment.dateOfBirth) return '';
      return new Date(this.enrollment.dateOfBirth).toLocaleDateString();
    },
    
    needsAgeGroup() {
      const plan = this.availablePlans.find(p => p.type === this.enrollment.planType);
      return plan && plan.requiresAge;
    },
    
    calculatedTotal() {
      const monthlyTotal = this.enrollment.payment.monthlyFee * this.enrollment.payment.monthsPaid;
      return monthlyTotal + this.enrollment.payment.applicationFee;
    },
    
    canSubmit() {
      return this.enrollment.signature && 
             this.enrollment.signatureDate && 
             this.personalFormValid && 
             this.contactFormValid && 
             this.paymentFormValid;
    }
  },
  
  mounted() {
    this.initSignatureCanvas();
  },
  
  methods: {
    selectPlan(plan) {
      this.enrollment.planType = plan.type;
      this.enrollment.payment.monthlyFee = plan.startingPrice;
      this.enrollment.payment.applicationFee = plan.applicationFee;
      this.calculatePayment();
    },
    
    calculatePayment() {
      const plan = this.availablePlans.find(p => p.type === this.enrollment.planType);
      if (!plan) return;
      
      // Simple calculation - you can make this more complex based on family size and age
      let monthlyFee = plan.startingPrice;
      
      // Adjust based on family size
      if (this.enrollment.familySize === 'Couple') {
        monthlyFee = monthlyFee * 1.5;
      } else if (this.enrollment.familySize === 'Family') {
        monthlyFee = monthlyFee * 2;
      }
      
      this.enrollment.payment.monthlyFee = monthlyFee;
      this.enrollment.payment.applicationFee = plan.applicationFee;
      this.enrollment.payment.totalPayment = this.calculatedTotal;
    },
    
    addDependent() {
      if (this.enrollment.dependents.length < 4) {
        this.enrollment.dependents.push({
          firstName: '',
          lastName: '',
          dateOfBirth: null,
          relationship: 'Child',
          gender: 'M'
        });
        this.dependentPanels.push(this.enrollment.dependents.length - 1);
      }
    },
    
    removeDependent(index) {
      this.enrollment.dependents.splice(index, 1);
    },
    
    nextStep() {
      if (this.step < 6) {
        this.step++;
      }
    },
    
    prevStep() {
      if (this.step > 1) {
        this.step--;
      }
    },
    
    async saveDraft() {
      this.saving = true;
      try {
        const response = await axios.post('/api/fhc-enrollments', {
          ...this.enrollment,
          status: 'Draft'
        });
        
        this.$toast?.success('Draft saved successfully!') || console.log('Draft saved');
        this.enrollment._id = response.data._id;
      } catch (error) {
        console.error('Error saving draft:', error);
        this.$toast?.error('Error saving draft') || console.error('Error saving draft');
      } finally {
        this.saving = false;
      }
    },
    
    async submitEnrollment() {
      if (!this.canSubmit) {
        this.$toast?.warning('Please complete all required fields') || console.warn('Form incomplete');
        return;
      }
      
      this.submitting = true;
      try {
        // Save signature
        this.enrollment.signature = this.$refs.signatureCanvas.toDataURL();
        
        // Create or update enrollment
        let response;
        if (this.enrollment._id) {
          response = await axios.put(`/api/fhc-enrollments/${this.enrollment._id}`, {
            ...this.enrollment,
            status: 'Pending'
          });
        } else {
          response = await axios.post('/api/fhc-enrollments', {
            ...this.enrollment,
            status: 'Pending'
          });
        }
        
        this.submittedEnrollmentId = response.data._id;
        this.successDialog = true;
        
      } catch (error) {
        console.error('Error submitting enrollment:', error);
        this.$toast?.error('Error submitting enrollment') || console.error('Error submitting');
      } finally {
        this.submitting = false;
      }
    },
    
    resetForm() {
      this.successDialog = false;
      this.step = 1;
      this.enrollment = {
        firstName: '',
        lastName: '',
        dateOfBirth: null,
        ssn: '',
        driverLicense: '',
        maritalStatus: '',
        streetAddress: '',
        city: '',
        state: 'FL',
        zipCode: '',
        homePhone: '',
        cellPhone: '',
        email: '',
        employerName: '',
        workPhone: '',
        dependents: [],
        planType: '',
        ageGroup: '',
        familySize: 'Single',
        payment: {
          method: '',
          monthsPaid: 1,
          monthlyFee: 0,
          applicationFee: 0,
          totalPayment: 0
        },
        signature: '',
        signatureDate: new Date().toISOString().substr(0, 10),
        status: 'Draft'
      };
      this.clearSignature();
    },
    
    goToDashboard() {
      this.$router.push('/');
    },
    
    goToLogin() {
      this.$router.push('/login');
    },
    
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString();
    },
    
    // Signature pad methods
    initSignatureCanvas() {
      this.$nextTick(() => {
        const canvas = this.$refs.signatureCanvas;
        if (canvas) {
          canvas.width = canvas.offsetWidth;
          canvas.height = 200;
          this.signatureContext = canvas.getContext('2d');
          this.signatureContext.strokeStyle = '#000000';
          this.signatureContext.lineWidth = 2;
          this.signatureContext.lineCap = 'round';
        }
      });
    },
    
    startDrawing(event) {
      this.isDrawing = true;
      const coords = this.getCoordinates(event);
      this.signatureContext.beginPath();
      this.signatureContext.moveTo(coords.x, coords.y);
    },
    
    draw(event) {
      if (!this.isDrawing) return;
      event.preventDefault();
      const coords = this.getCoordinates(event);
      this.signatureContext.lineTo(coords.x, coords.y);
      this.signatureContext.stroke();
    },
    
    stopDrawing() {
      this.isDrawing = false;
    },
    
    getCoordinates(event) {
      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      
      let clientX, clientY;
      if (event.touches && event.touches.length > 0) {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }
      
      return {
        x: clientX - rect.left,
        y: clientY - rect.top
      };
    },
    
    clearSignature() {
      const canvas = this.$refs.signatureCanvas;
      if (canvas && this.signatureContext) {
        this.signatureContext.clearRect(0, 0, canvas.width, canvas.height);
        this.enrollment.signature = '';
      }
    }
  }
};
</script>

<style scoped>
.fhc-enrollment {
  background: #f5f5f5;
  min-height: 100vh;
  padding: 20px;
}

.header-card {
  margin-bottom: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
}

.plan-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.plan-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.2) !important;
}

.selected-plan {
  border: 2px solid #1976D2;
  background: #E3F2FD;
}

.benefits-list {
  margin-top: 10px;
}

.summary-section {
  margin-bottom: 16px;
}

.summary-label {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  font-weight: 500;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 16px;
  color: #333;
  font-weight: 500;
}

.signature-pad-container {
  border: 2px dashed #ccc;
  border-radius: 4px;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
}

.signature-canvas {
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: crosshair;
  background: white;
  width: 100%;
  max-width: 600px;
  touch-action: none;
}

.signature-canvas:hover {
  border-color: #1976D2;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .plan-card {
    margin-bottom: 16px;
  }
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.v-stepper-content {
  animation: fadeIn 0.3s ease;
}
</style>

